import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { getMomentTz } from "@dexteel/mesf-core";
import {
	Box,
	FormControlLabel,
	Paper,
	Radio,
	RadioGroup,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormQuestion } from "../models/FormQuestion";
import { FormSection } from "../models/FormSection";
import { upsertAnswer } from "../repositories/FormsRepository";

const moment = getMomentTz();

interface QuestionFlow {
	section: FormSection;
	question: FormQuestion;
	questionKey: number;
}

export const Questions = ({
	formSections,
	currentSection,
	isSubmitLoading,
	formInstanceId,
	setIsSubmitLoading,
	setCanNext,
	onSuccess,
}: {
	formSections: FormSection[];
	currentSection: FormSection | null;
	isSubmitLoading: boolean;
	formInstanceId: number;
	setIsSubmitLoading: (isSubmitLoading: boolean) => void;
	setCanNext: (canNext: boolean) => void;
	onSuccess: (error?: string) => void;
}) => {
	const theme = useTheme();

	const [formInitialized, setFormInitialized] = useState(false);
	const [questionsToShow, setQuestionsToShow] = useState<QuestionFlow[]>([]);

	const { control, watch, reset } = useForm<any>({
		defaultValues: {},
	});

	const watchedValues = watch();

	const getQuestionValue = (question: FormQuestion) => {
		switch (question.QuestionTypeCode) {
			case "R":
				return question.RadioButtonSelected || "";
			case "D":
				return question.DatetimeContent || "";
			case "T":
				return question.TextBoxContent || "";
			case "N":
				return question.NumericalContent || "";
			default:
				return "";
		}
	};

	const initializeFormValues = (sections: FormSection[]) => {
		const initialValues: any = {};

		sections.forEach((section) => {
			section.Questions.forEach((question) => {
				const existingValue = getQuestionValue(question);
				if (existingValue !== "" && existingValue != null) {
					initialValues[`question_${question.QuestionId}`] = existingValue;
				}
			});
		});

		return initialValues;
	};

	const updateAnswer = useMutation(
		({
			questionId,
			answer,
		}: {
			questionId: number | null;
			answer: string | number | undefined | null;
		}) =>
			upsertAnswer({
				formInstanceId,
				questionId,
				answer,
			}),
		{
			onSuccess: () => onSuccess(),
			onError: (error: Error) => onSuccess(error.message),
			onSettled: () => setIsSubmitLoading(false),
		},
	);

	const getAllQuestionsMap = (sections: FormSection[]) => {
		const map = new Map<
			number,
			{ section: FormSection; question: FormQuestion }
		>();
		sections.forEach((section) => {
			section.Questions.forEach((question) => {
				map.set(question.QuestionId, { section, question });
			});
		});
		return map;
	};

	const buildSectionQuestionFlow = (
		section: FormSection | null,
	): QuestionFlow[] => {
		const flow: QuestionFlow[] = [];
		if (!section) return flow;

		let currentQuestionId: number | null = null;
		const allQuestionsMap = getAllQuestionsMap(formSections);

		const firstQuestionInSection = section.Questions.find(
			(q) => q.IsFirstQuestion,
		);

		if (firstQuestionInSection) {
			currentQuestionId = firstQuestionInSection.QuestionId;
		} else if (section.Questions.length > 0) {
			currentQuestionId = section.Questions[0].QuestionId;
		}

		while (currentQuestionId !== null) {
			const questionData = allQuestionsMap.get(currentQuestionId);

			if (
				!questionData ||
				questionData.section.SectionId !== section.SectionId
			) {
				currentQuestionId = null;
				break;
			}

			const { question } = questionData;
			const questionKey = question.QuestionId;

			flow.push({ section, question, questionKey });

			const currentResponse = watchedValues[`question_${questionKey}`];
			let nextQuestionId: number | null = null;

			if (
				question.QuestionTypeCode === "R" &&
				question.Options &&
				currentResponse != undefined
			) {
				const selectedOption = question.Options.find(
					(opt) => String(opt.OptionId) === String(currentResponse),
				);
				nextQuestionId =
					selectedOption?.NextQuestionId || question.NextQuestionId;
			} else {
				nextQuestionId = question.NextQuestionId;
			}

			currentQuestionId = nextQuestionId;
		}

		return flow;
	};

	const checkCanSubmit = (questions: QuestionFlow[]) => {
		if (questions.length === 0) {
			setCanNext(false);
			return;
		}

		const allRequiredAnswered = questions.every(({ question }) => {
			if (!question.IsRequired) return true;
			const response = watchedValues[`question_${question.QuestionId}`];
			return response != undefined && String(response).trim() !== "";
		});

		setCanNext(allRequiredAnswered);
	};

	useEffect(() => {
		if (formInitialized) {
			const newQuestionFlow = buildSectionQuestionFlow(currentSection);
			setQuestionsToShow(newQuestionFlow);
			checkCanSubmit(newQuestionFlow);
		}
	}, [currentSection, formInitialized]);

	useEffect(() => {
		if (formSections && formSections.length > 0) {
			const initialValues = initializeFormValues(formSections);
			reset(initialValues);
			setFormInitialized(true);
		}
	}, [formSections]);

	const renderQuestionControl = (question: FormQuestion) => {
		const fieldName = `question_${question.QuestionId}`;

		switch (question.QuestionTypeCode) {
			case "R":
				return (
					<Controller
						name={fieldName}
						control={control}
						render={({ field: { value, onChange } }) => (
							<RadioGroup
								name={`question-${question.QuestionId}`}
								value={Number(value) || null}
								onChange={(e) => {
									onChange(e.target.value);
									setIsSubmitLoading(true);
									updateAnswer.mutate({
										questionId: question.QuestionId!,
										answer: e.target.value,
									});
								}}
							>
								{question.Options?.sort(
									(a, b) => (a?.SortOrder || 0) - (b?.SortOrder || 0),
								).map((option) => (
									<FormControlLabel
										key={option.OptionId}
										value={option.OptionId}
										label={option.OptionText}
										control={<Radio color="primary" />}
										disabled={isSubmitLoading}
									/>
								))}
								{(!question.Options || question.Options.length === 0) && (
									<Typography color="error">
										No options available for this question
									</Typography>
								)}
							</RadioGroup>
						)}
					/>
				);

			case "T":
				return (
					<Controller
						name={fieldName}
						control={control}
						render={({ field: { value, onChange } }) => (
							<TextField
								label="Enter your answer"
								fullWidth
								value={value || ""}
								onChange={(e) => onChange(e.target.value)}
								onBlur={() => {
									setIsSubmitLoading(true);
									updateAnswer.mutate({
										questionId: question.QuestionId!,
										answer: value,
									});
								}}
								onKeyDown={(e) => {
									if (e.code === "Enter") {
										setIsSubmitLoading(true);
										updateAnswer.mutate({
											questionId: question.QuestionId!,
											answer: value,
										});
									}
								}}
								variant="outlined"
								margin="none"
								size="small"
								style={{ maxWidth: "65%" }}
								disabled={isSubmitLoading}
							/>
						)}
					/>
				);

			case "D":
				return (
					<Controller
						name={fieldName}
						control={control}
						render={({ field: { value, onChange } }) => (
							<DatePicker
								label="Select date"
								format="MM/DD/YYYY"
								views={["year", "month", "day"]}
								value={value ? moment(value) : null}
								onChange={(newValue) => {
									if (newValue && newValue.isValid()) {
										const formattedValue = newValue.format("MM/DD/YYYY");
										if (
											formattedValue.length === 10 &&
											newValue.year() > 1000
										) {
											onChange(newValue.toISOString());
											setIsSubmitLoading(true);
											updateAnswer.mutate({
												questionId: question.QuestionId!,
												answer: newValue.toISOString(),
											});
										}
									} else if (newValue === null) {
										onChange(null);
									}
								}}
								disabled={isSubmitLoading}
								slots={{
									textField: (params) => (
										<TextField
											{...params}
											size="small"
											margin="none"
											variant="outlined"
											fullWidth
											style={{ maxWidth: "50%" }}
											error={false}
										/>
									),
								}}
							/>
						)}
					/>
				);

			case "N":
				return (
					<Controller
						name={fieldName}
						control={control}
						render={({ field: { value, onChange } }) => (
							<TextField
								label="Enter number"
								type="number"
								fullWidth
								value={value || ""}
								onChange={(e) => onChange(e.target.value)}
								onBlur={() => {
									setIsSubmitLoading(true);
									updateAnswer.mutate({
										questionId: question.QuestionId!,
										answer: value,
									});
								}}
								onKeyDown={(e) => {
									if (e.code === "Enter") {
										setIsSubmitLoading(true);
										updateAnswer.mutate({
											questionId: question.QuestionId!,
											answer: value,
										});
									}
								}}
								variant="outlined"
								margin="none"
								size="small"
								style={{ maxWidth: "50%" }}
								disabled={isSubmitLoading}
							/>
						)}
					/>
				);

			default:
				return <Typography color="error">Unsupported question type</Typography>;
		}
	};

	if (!currentSection || questionsToShow.length === 0) {
		return <CenteredLazyLoading />;
	}

	return (
		<Box>
			<Box
				sx={{
					padding: (theme) => theme.spacing(2),
				}}
			>
				{questionsToShow.map(({ question, questionKey }, index) => (
					<Box key={questionKey}>
						<Paper
							sx={{
								padding: (theme) => theme.spacing(3),
								borderRadius: (theme) => theme.shape.borderRadius,
								marginBottom: (theme) => theme.spacing(2),
								border: (theme) => `1px solid ${theme.palette.divider}`,
							}}
							elevation={1}
						>
							<Typography
								fontWeight={500}
								sx={{
									marginBottom: (theme) => theme.spacing(2),
								}}
								variant="h6"
							>
								{index + 1}. {question.QuestionText}
								{question.IsRequired && (
									<span
										style={{
											color: theme.palette.error.main,
											marginLeft: theme.spacing(0.5),
										}}
									>
										*
									</span>
								)}
							</Typography>
							{renderQuestionControl(question)}
						</Paper>
					</Box>
				))}
			</Box>
		</Box>
	);
};
